import axios from "axios";
import constants from "./api_urls";
import { refreshTokenEventEmitter } from "./sessionEvents";

// Optional global navigation handler (wired from React using useNavigate)
let globalNavigateHandler = null;
export const setGlobalNavigate = (handler) => {
  globalNavigateHandler = handler;
};

// Base URLs derived from existing constants
export const BASE_API_URL = constants.baseUrl;
export const PUBLIC_BASE_API_URL = constants.mainUrlPublic;
export const API_V1_URL = constants.mainUrl; // includes /api prefix

// Standard JSON headers used across the app
export const API_HEADERS = {
  JSON: {
    "Content-Type": "application/json",
  },
};

// Centralised endpoints used by the interceptor / ApiService
export const API_ENDPOINTS = {
  auth: {
    refreshToken: "/v1/auth/refresh/recruiter",
    logout: "/v1/auth/logout/recruiter",
  },
};

// Clears the httpOnly cookie by hitting the logout endpoint, then redirects to /login.
// Using a plain axios call (no interceptors) to avoid loops.
export const forceLogoutAndRedirect = () => {
  // Already on the login page — clearing storage is enough, no redirect needed.
  // Redirecting here would cause an infinite reload loop because the login page
  // itself calls getCurrentUser() on mount, which triggers a 401 → refresh fail → here.
  if (typeof window !== "undefined" && window.location.pathname === "/login") {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}
    return;
  }

  const logoutUrl = API_V1_URL + API_ENDPOINTS.auth.logout;
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (e) {}

  // Best-effort: tell the server to clear the session cookie.
  // We don't await this – redirect happens regardless.
  axios
    .post(logoutUrl, {}, { withCredentials: true, headers: API_HEADERS.JSON })
    .catch(() => {})
    .finally(() => {
      try {
        if (typeof globalNavigateHandler === "function") {
          globalNavigateHandler();
        } else {
          window.location.replace("/login");
        }
      } catch (e) {
        console.error(e);
      }
    });
};

let isRefreshing = false;
let sessionExpiredFired = false;
let failedQueue = [];

const processQueue = (error, token) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create the global Axios instance
export const apiClient = axios.create({
  baseURL: constants.isTestServer ? constants.testUrl : constants.baseUrl,
  withCredentials: true,
  headers: {
    ...API_HEADERS.JSON,
  },
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    if (!originalRequest) return Promise.reject(error);

    const url = originalRequest.url || "";
    // Check for 401 or 400 for token expiration/missing
    if (
      error.response &&
      error.response.status === 401 &&
      !url.includes("/login") &&
      !url.includes("/refresh") &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshUrl = API_V1_URL + API_ENDPOINTS.auth.refreshToken;
        // directly hit refresh endpoint using axios without interceptors to avoid loops
        const refreshResponse = await axios.post(
          refreshUrl,
          {},
          {
            withCredentials: true,
            headers: API_HEADERS.JSON,
          },
        );

        if (refreshResponse.status >= 200 && refreshResponse.status < 300) {
          processQueue(null, null);
          isRefreshing = false;
          sessionExpiredFired = false;
          return apiClient(originalRequest);
        } else {
          isRefreshing = false;
          const err = new Error("Session expired");
          processQueue(err, null);
          if (!sessionExpiredFired) {
            sessionExpiredFired = true;
            forceLogoutAndRedirect();
          }
          return Promise.reject(err);
        }
      } catch (err) {
        isRefreshing = false;
        processQueue(err, null);
        if (!sessionExpiredFired) {
          sessionExpiredFired = true;
          forceLogoutAndRedirect();
        }
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  },
);

class ApiService {
  constructor() {
    this.baseUrl = BASE_API_URL;
    this.publicUrl = PUBLIC_BASE_API_URL;
    this.v1Url = API_V1_URL;
  }

  static getInstance() {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /** Unified request handler mapping to Axios */
  async request(url, options, isPublic = false, retry = true, isV1 = false) {
    let fullUrl = (isPublic ? this.publicUrl : this.baseUrl) + url;
    if (isV1) {
      fullUrl = this.v1Url + url;
    }

    try {
      const response = await apiClient.request({
        url: fullUrl,
        ...options,
      });
      return response.data;
    } catch (error) {
      // Keep existing behavior: return error data if any, or shape an error object
      if (error.response && error.response.data) {
        return { status: error.response.status, ...error.response.data };
      }
      return { status: 500, message: error.message };
    }
  }

  get(endpoint, params) {
    return this.request(endpoint, { method: "GET", params });
  }

  getV1(endpoint, params) {
    return this.request(endpoint, { method: "GET", params }, false, true, true);
  }

  getPublic(endpoint, params) {
    return this.request(endpoint, { method: "GET", params }, true);
  }

  post(endpoint, data) {
    return this.request(endpoint, { method: "POST", data });
  }

  postV1(endpoint, data) {
    return this.request(endpoint, { method: "POST", data }, false, true, true);
  }

  postPublic(endpoint, data) {
    return this.request(endpoint, { method: "POST", data }, true);
  }

  put(endpoint, data) {
    return this.request(endpoint, { method: "PUT", data });
  }

  putV1(endpoint, data) {
    return this.request(endpoint, { method: "PUT", data }, false, true, true);
  }

  delete(endpoint, data) {
    return this.request(endpoint, { method: "DELETE", data });
  }

  deleteV1(endpoint, data) {
    return this.request(
      endpoint,
      { method: "DELETE", data },
      false,
      true,
      true,
    );
  }

  async logout(endpoint) {
    return this.request(endpoint, { method: "POST" }, true);
  }

  async postFile(endpoint, data, params, isV1 = false) {
    const fullUrl = (isV1 ? this.v1Url : this.baseUrl) + endpoint;

    try {
      const response = await apiClient.post(fullUrl, data, {
        params,
        responseType: "blob",
      });

      // Extract progress headers
      const headers = response.headers || {};
      const progress = {
        totalRecords: headers["x-total-records"]
          ? parseInt(headers["x-total-records"], 10)
          : undefined,
        totalChunks: headers["x-total-chunks"]
          ? parseInt(headers["x-total-chunks"], 10)
          : undefined,
        processedRecords: headers["x-processed-records"]
          ? parseInt(headers["x-processed-records"], 10)
          : undefined,
        currentChunk: headers["x-progress"] || undefined,
      };

      const blob = response.data;
      const contentDisposition = headers["content-disposition"];
      let filename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['\"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      return { blob, filename, progress };
    } catch (error) {
      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data instanceof Blob
      ) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          if (
            errorData.message &&
            errorData.message.includes("Refresh token")
          ) {
            refreshTokenEventEmitter.dispatchEvent(
              new CustomEvent("show-refresh-token-popup", {
                detail: {
                  message: errorData.message,
                  status: error.response.status,
                },
              }),
            );
          }
          throw new Error(errorData.message || "Failed to download file");
        } catch (e) {
          throw new Error(
            "Failed to download file: " +
              (error.response.statusText || "Unknown error"),
          );
        }
      }
      throw new Error(error.message || "Download failed");
    }
  }
}

export const apiService = ApiService.getInstance();

// Backwards compatibility: keep default export as the axios client
const httpClient = apiClient;
export default httpClient;
